'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { cn } from '@/lib/utils';

export type FeatureProcessOverviewProps =
  SliceComponentProps<Content.FeatureProcessOverviewSlice>;

type Step =
  Content.FeatureProcessOverviewSliceWithCardSliderStepsPrimaryStepsItem;

const FeatureProcessOverview: FC<FeatureProcessOverviewProps> = ({ slice }) => {
  const {
    eyebrow,
    title,
    subtitle,
    highlighted_message,
    primary_cta,
    steps,
    shared_image,
  } = slice.primary;

  const [activeIndex, setActiveIndex] = useState(0);
  const rowRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageLayerRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Aligns each card's background image so collectively they read as one
  // continuous picture: the same image is rendered inside every card but
  // sized to the whole row and offset by each card's position, so the
  // rounded card frames act like windows into a single image.
  const alignSharedImage = useCallback(() => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    const rowRect = row.getBoundingClientRect();
    cardRefs.current.forEach((card, i) => {
      const layer = imageLayerRefs.current[i];
      if (!card || !layer) {
        return;
      }
      const cardRect = card.getBoundingClientRect();
      layer.style.width = `${rowRect.width}px`;
      layer.style.height = `${rowRect.height}px`;
      layer.style.left = `${-(cardRect.left - rowRect.left)}px`;
      layer.style.top = `${-(cardRect.top - rowRect.top)}px`;
    });
  }, []);

  useEffect(() => {
    alignSharedImage();
    const ro = new ResizeObserver(alignSharedImage);
    if (rowRef.current) {
      ro.observe(rowRef.current);
    }
    cardRefs.current.forEach((c) => c && ro.observe(c));
    window.addEventListener('resize', alignSharedImage);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', alignSharedImage);
    };
  }, [alignSharedImage, steps.length, activeIndex]);

  const goNext = useCallback(
    () => setActiveIndex((i) => (i + 1) % steps.length),
    [steps.length],
  );
  const goPrev = useCallback(
    () => setActiveIndex((i) => (i - 1 + steps.length) % steps.length),
    [steps.length],
  );

  const renderStatus = (step: Step) => {
    if (!step.step_number_and_title) {
      return null;
    }
    return (
      <span className="whitespace-nowrap font-inter text-[14px] md:text-[18px] font-normal uppercase leading-[1.2] text-white">
        {step.step_number_and_title}
      </span>
    );
  };

  const renderActiveContent = (step: Step) => (
    <>
      <div className="flex shrink-0 items-center gap-6 self-end">
        {renderStatus(step)}
        <span
          aria-hidden="true"
          className="size-3 shrink-0 rounded-full border border-white"
        />
      </div>

      <div className="flex flex-1 min-h-0 flex-col justify-between gap-6">
        <div className="flex flex-col items-start gap-4">
          {isFilled.richText(step.step_label) && (
            <div className="inline-flex items-center rounded-[89px] border border-[#00F557] px-4 py-2 text-[14px] md:text-[18px] font-normal uppercase leading-[1.2] text-white [&_p]:m-0">
              <PrismicRichText field={step.step_label} />
            </div>
          )}
          {step.step_headline && (
            <h3 className="m-0 text-[28px] md:text-[42px] font-semibold leading-[1.2] text-white">
              {step.step_headline}
            </h3>
          )}
          {isFilled.richText(step.step_description) && (
            <div className="text-[15px] md:text-[16px] font-light leading-[1.5] text-white [&_p]:m-0 [&_p+p]:mt-2">
              <PrismicRichText field={step.step_description} />
            </div>
          )}
          {step.step_status && (
            <p className="m-0 text-[18px] md:text-[20px] font-medium leading-[1.5] text-[#00F557]">
              {step.step_status}
            </p>
          )}
        </div>

        <div className="flex items-center gap-6 md:gap-[60px]">
          <div className="flex flex-1 items-center">
            <span className="h-px w-[89px] bg-white" />
            <span className="h-px flex-1 bg-white/30" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Previous step"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="flex size-9 items-center justify-center rounded-full border border-white/60 text-white transition-colors hover:border-white hover:bg-white/10"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8L10 13"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Next step"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="flex size-9 items-center justify-center rounded-full border border-white/60 text-white transition-colors hover:border-white hover:bg-white/10"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 3L11 8L6 13"
                  stroke="currentColor"
                  strokeWidth="1"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderCollapsedContent = (step: Step, i: number) => (
    <>
      <div
        className="flex flex-1 items-center justify-center"
        style={{ containerType: 'size' }}
      >
        <div className="flex w-[100cqh] flex-none -rotate-90 items-center justify-center gap-6 whitespace-nowrap">
          {renderStatus(step)}
          <span
            aria-hidden="true"
            className="size-3 shrink-0 rounded-full border border-white"
          />
        </div>
      </div>
      <button
        type="button"
        aria-label={`Expand step ${step.step_number_and_title || i + 1}`}
        tabIndex={-1}
        className="flex size-9 items-center justify-center rounded-full border border-white/60 text-white transition-colors group-hover:border-white group-hover:bg-white/10"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </button>
    </>
  );

  const hasImage = isFilled.image(shared_image);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white font-inter px-[30px] py-[30px]"
    >
      <div className="mx-auto max-w-[1380px] overflow-hidden rounded-[24px] bg-[#041118] px-6 py-16 md:px-[60px] md:py-20 lg:px-[150px]">
        {/* Header */}
        <div className="mx-auto flex max-w-[1080px] flex-col items-center gap-6 pb-[60px] text-center">
          {eyebrow && (
            <span className="inline-flex rounded-[89px] border border-[#00F557] px-4 py-2 text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}
          {isFilled.richText(title) && (
            <div className="text-[32px] md:text-[42px] font-semibold leading-[1.2] capitalize text-white [&_h1]:m-0 [&_h2]:m-0">
              <PrismicRichText field={title} />
            </div>
          )}
          {isFilled.richText(subtitle) && (
            <div className="text-[15px] md:text-[16px] font-light leading-[1.4] text-white [&_p]:m-0 [&_p+p]:mt-3">
              <PrismicRichText field={subtitle} />
            </div>
          )}
          {isFilled.richText(highlighted_message) && (
            <div className="text-[18px] md:text-[20px] font-medium leading-[1.4] text-[#00F557] [&_p]:m-0">
              <PrismicRichText field={highlighted_message} />
            </div>
          )}
          {isFilled.link(primary_cta) && (
            <PrismicNextLink
              field={primary_cta}
              className="mt-2 inline-flex items-center justify-center rounded-[100px] border border-white px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-white transition-colors hover:bg-white hover:text-[#041118]"
            >
              {primary_cta.text || 'Learn more'}
            </PrismicNextLink>
          )}
        </div>

        {/* Desktop: horizontal card row */}
        {steps.length > 0 && (
          <div ref={rowRef} className="relative hidden h-[550px] gap-2 lg:flex">
            {steps.map((step, i) => {
              const isActive = i === activeIndex;
              return (
                <div
                  key={i}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isActive}
                  aria-label={
                    step.step_headline ??
                    step.step_number_and_title ??
                    `Step ${i + 1}`
                  }
                  onClick={() => !isActive && setActiveIndex(i)}
                  onKeyDown={(e) => {
                    if (!isActive && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setActiveIndex(i);
                    }
                  }}
                  className={cn(
                    'group relative h-[550px] overflow-hidden border-2 border-white/70 transition-[flex,border-radius,transform] duration-700 ease-in-out',
                    isActive
                      ? 'flex-[1_1_0%] min-w-0 rounded-[100px]'
                      : 'flex-[0_0_136px] cursor-pointer rounded-full hover:flex-[0_0_156px]',
                  )}
                >
                  {/* Shared image layer — positioned so the whole row shows
                      one continuous image clipped by each card's frame. */}
                  {hasImage && (
                    <div
                      ref={(el) => {
                        imageLayerRefs.current[i] = el;
                      }}
                      aria-hidden="true"
                      className="pointer-events-none absolute"
                    >
                      <PrismicNextImage
                        field={shared_image}
                        fill
                        sizes="(min-width: 1024px) 1080px, 100vw"
                        className="object-cover"
                        fallbackAlt=""
                      />
                    </div>
                  )}

                  {/* Darkening + blur overlay — less on active, more on collapsed */}
                  <div
                    aria-hidden="true"
                    className={cn(
                      'pointer-events-none absolute inset-0 transition-all duration-700',
                      isActive
                        ? 'bg-black/40 backdrop-blur-0'
                        : 'bg-[rgba(5,29,25,0.7)] backdrop-blur-[21px]',
                    )}
                  />

                  {/* Content */}
                  <div
                    className={cn(
                      'relative z-10 flex h-full w-full flex-col',
                      isActive
                        ? 'items-stretch gap-[60px] px-10 py-[60px] xl:px-[100px]'
                        : 'items-center justify-between py-[60px]',
                    )}
                  >
                    {isActive
                      ? renderActiveContent(step)
                      : renderCollapsedContent(step, i)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Mobile / tablet: vertical stack */}
        {steps.length > 0 && (
          <div className="flex flex-col gap-4 lg:hidden">
            {steps.map((step, i) => {
              const isActive = i === activeIndex;
              return (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isActive}
                  onClick={() => setActiveIndex(isActive ? -1 : i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveIndex(isActive ? -1 : i);
                    }
                  }}
                  className={cn(
                    'relative overflow-hidden rounded-[40px] border-2 border-white/70 transition-all duration-500',
                    isActive
                      ? 'min-h-[420px] bg-black/40'
                      : 'h-[72px] cursor-pointer bg-[rgba(5,29,25,0.7)] backdrop-blur-[21px]',
                  )}
                >
                  {hasImage && isActive && (
                    <PrismicNextImage
                      field={shared_image}
                      fill
                      sizes="100vw"
                      className="pointer-events-none absolute inset-0 -z-10 object-cover"
                      fallbackAlt=""
                    />
                  )}
                  {isActive && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 -z-10 bg-black/40"
                    />
                  )}

                  {!isActive && (
                    <div className="flex h-full items-center justify-between px-6">
                      {renderStatus(step)}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M10 4V16M4 10H16"
                          stroke="white"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </div>
                  )}
                  {isActive && (
                    <div className="flex min-h-[420px] flex-col gap-6 p-6">
                      <div className="flex items-center justify-between">
                        {renderStatus(step)}
                        <span
                          aria-hidden="true"
                          className="size-3 shrink-0 rounded-full border border-white"
                        />
                      </div>
                      <div className="flex flex-col items-start gap-3">
                        {isFilled.richText(step.step_label) && (
                          <div className="inline-flex items-center rounded-[89px] border border-[#00F557] px-3 py-1.5 text-[13px] font-normal uppercase leading-[1.2] text-white [&_p]:m-0">
                            <PrismicRichText field={step.step_label} />
                          </div>
                        )}
                        {step.step_headline && (
                          <h3 className="m-0 text-[26px] font-semibold leading-[1.2] text-white">
                            {step.step_headline}
                          </h3>
                        )}
                        {isFilled.richText(step.step_description) && (
                          <div className="text-[15px] font-light leading-[1.5] text-white [&_p]:m-0 [&_p+p]:mt-2">
                            <PrismicRichText field={step.step_description} />
                          </div>
                        )}
                        {step.step_status && (
                          <p className="m-0 text-[16px] font-medium leading-[1.5] text-[#00F557]">
                            {step.step_status}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureProcessOverview;
